// Perfil metrológico para fuerza y torque.
// Convención única del sistema: error = indicación del instrumento - valor del patrón.

const SQRT3 = Math.sqrt(3);
const SQRT12 = Math.sqrt(12);

export const FORCE_TORQUE_TEMPLATES = {
  torque: {
    code: 'FORCE:torque_wrench:v2',
    revision: '2',
    family: 'force',
    subfamily: 'torque',
    title: 'Calibración de torquímetros',
    unit: 'Nm',
    pointCount: 3,
    repeatCount: 5,
    method: 'Por comparación directa con un patrón de torque.',
    procedure: 'P-DT-PT-17-00 (Calibración de Torquímetros)',
  },
  dynamometer: {
    code: 'FORCE:dynamometer:v2',
    revision: '2',
    family: 'force',
    subfamily: 'force',
    title: 'Calibración de dinamómetros y tensiómetros',
    unit: 'N',
    pointCount: 5,
    repeatCount: 5,
    method: 'Por comparación directa con un patrón de fuerza.',
    procedure: 'Procedimiento técnico de Fuerza aplicable al instrumento',
  },
  load_cell: {
    code: 'FORCE:load_cell:v2',
    revision: '2',
    family: 'force',
    subfamily: 'force',
    title: 'Calibración de células de carga',
    unit: 'N',
    pointCount: 5,
    repeatCount: 5,
    method: 'Por comparación directa con un patrón de fuerza.',
    procedure: 'Procedimiento técnico de Fuerza aplicable al instrumento',
  },
};

export const DEFAULT_FORCE_FAMILY_DATA = {
  profile_key: 'force_torque_comparison',
  profile_label: 'Fuerza / Torque · Comparación directa',
  subfamily: 'torque',
  method: FORCE_TORQUE_TEMPLATES.torque.method,
  procedure: FORCE_TORQUE_TEMPLATES.torque.procedure,
  point_count: FORCE_TORQUE_TEMPLATES.torque.pointCount,
  repeat_count: FORCE_TORQUE_TEMPLATES.torque.repeatCount,
  directions: ['CW'],
  tool_type: 'II',
  tool_class: '',
  tolerance_mode: 'class',
  tolerance_percent: 4,
  decision_rule: 'guard_band',
  original_instrument_unit: 'Nm',
  values_normalized_to: 'Nm',
  standard_configuration: { reference: null },
  standard_budget: {
    drift_percent: 0,
    temperature_coefficient_percent_per_10c: 0.4,
    alignment_uncertainty_percent: 0,
    other_uncertainty_percent: 0,
  },
  environment_readings: [
    { key: 'start_1', label: 'Inicio 1.er ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'start_2', label: 'Inicio 2.º ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
    { key: 'end_2', label: 'Fin 2.º ciclo', temp_c: null, pressure_hpa: null, humidity_rh: null },
  ],
  result_details: [],
};

export function finiteOrNull(value) {
  if (value === '' || value === null || value === undefined) return null;
  const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

function normalizeText(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function normalizeForceUnit(value) {
  return normalizeText(value)
    .replace(/newton(?:s)?/g, 'n')
    .replace(/meters?|metres?|metros?/g, 'm')
    .replace(/feet|foot|pies?/g, 'ft')
    .replace(/inches|inch|pulgadas?|pulg\.?/g, 'in')
    .replace(/[·.]/g, '')
    .replace(/\s+/g, '')
    .replace(/-/g, '');
}

const TORQUE_TO_NM = {
  nm: 1,
  ncm: 0.01,
  kgfm: 9.80665,
  kgfcm: 0.0980665,
  lbfft: 1.3558179483314004,
  lbft: 1.3558179483314004,
  lbfin: 0.1129848290276167,
  lbin: 0.1129848290276167,
};

const FORCE_TO_N = {
  n: 1,
  kn: 1000,
  kgf: 9.80665,
  gf: 0.00980665,
  lbf: 4.4482216152605,
};

export function isTorqueUnit(unit) {
  return Object.prototype.hasOwnProperty.call(TORQUE_TO_NM, normalizeForceUnit(unit));
}

export function isLinearForceUnit(unit) {
  return Object.prototype.hasOwnProperty.call(FORCE_TO_N, normalizeForceUnit(unit));
}

export function forceValueToBase(value, unit, subfamily = 'torque') {
  const numeric = finiteOrNull(value);
  if (numeric === null) return null;
  const normalized = normalizeForceUnit(unit);
  const table = subfamily === 'torque' ? TORQUE_TO_NM : FORCE_TO_N;
  const factor = table[normalized];
  if (!factor) throw new Error(`La unidad ${unit || '(sin unidad)'} no es compatible con ${subfamily === 'torque' ? 'Torque' : 'Fuerza'}.`);
  return numeric * factor;
}

export function resolveForceProfile(instrument = {}, templateCode = '') {
  const code = String(templateCode || '');
  const type = normalizeText(instrument?.type);
  const category = normalizeText(instrument?.category?.name || instrument?.category_name);
  const combined = `${type} ${category}`;
  if (code.includes('torque') || combined.includes('torqu') || isTorqueUnit(instrument?.unit)) {
    return FORCE_TORQUE_TEMPLATES.torque;
  }
  if (code.includes('load_cell') || combined.includes('celula') || combined.includes('load cell')) {
    return FORCE_TORQUE_TEMPLATES.load_cell;
  }
  return FORCE_TORQUE_TEMPLATES.dynamometer;
}

export function mergeForceFamilyData(value = {}) {
  const input = value && typeof value === 'object' ? value : {};
  const environment = Array.isArray(input.environment_readings)
    ? input.environment_readings
    : DEFAULT_FORCE_FAMILY_DATA.environment_readings;
  const directions = Array.isArray(input.directions) && input.directions.length
    ? input.directions.map((item) => String(item).toUpperCase()).filter((item) => ['CW', 'CCW', 'LOAD'].includes(item))
    : DEFAULT_FORCE_FAMILY_DATA.directions;
  return {
    ...DEFAULT_FORCE_FAMILY_DATA,
    ...input,
    directions: [...new Set(directions)],
    standard_configuration: {
      ...DEFAULT_FORCE_FAMILY_DATA.standard_configuration,
      ...(input.standard_configuration || {}),
    },
    standard_budget: {
      ...DEFAULT_FORCE_FAMILY_DATA.standard_budget,
      ...(input.standard_budget || {}),
    },
    environment_readings: DEFAULT_FORCE_FAMILY_DATA.environment_readings.map((fallback, index) => ({
      ...fallback,
      ...(environment[index] || environment.find((item) => item?.key === fallback.key) || {}),
    })),
    result_details: Array.isArray(input.result_details) ? input.result_details : [],
  };
}

export function buildForceBlock(direction = 'CW', profile = FORCE_TORQUE_TEMPLATES.torque) {
  const normalized = profile.subfamily === 'torque' ? String(direction || 'CW').toUpperCase() : 'LOAD';
  const label = normalized === 'CW' ? 'Sentido horario' : normalized === 'CCW' ? 'Sentido antihorario' : 'Carga';
  return {
    block_key: `force_torque_${normalized.toLowerCase()}`,
    display_name: label,
    unit: `[${profile.unit}]`,
    applicable: true,
    point_count: profile.pointCount,
    sort_order: normalized === 'CCW' ? 1 : 0,
    mode: normalized,
  };
}

export function suggestForceSetpoints(rangeMin, rangeMax, pointCount = 3, subfamily = 'torque') {
  const min = finiteOrNull(rangeMin) ?? 0;
  const max = finiteOrNull(rangeMax);
  if (max === null || max <= min) return Array.from({ length: pointCount }, () => '');
  const fractions = subfamily === 'torque'
    ? (pointCount <= 3 ? [0.2, 0.6, 1] : [0.2, 0.4, 0.6, 0.8, 1])
    : (pointCount <= 3 ? [0.2, 0.6, 1] : [0.2, 0.4, 0.6, 0.8, 1]);
  return fractions.slice(0, pointCount).map((fraction) => {
    const target = Math.max(min, max * fraction);
    return Number(target.toPrecision(9));
  });
}

function mean(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
}

function sampleStandardDeviation(values = []) {
  const valid = values.map(finiteOrNull).filter((value) => value !== null);
  if (valid.length < 2) return 0;
  const average = mean(valid);
  return Math.sqrt(valid.reduce((sum, value) => sum + ((value - average) ** 2), 0) / (valid.length - 1));
}

function rss(values = []) {
  return Math.sqrt(values.reduce((sum, value) => sum + ((finiteOrNull(value) ?? 0) ** 2), 0));
}

function standardResultValue(result = {}) {
  return finiteOrNull(result.reference_value) ?? finiteOrNull(result.indicated_value);
}

function interpolateStandardUncertainty(snapshot = {}, value) {
  const target = Math.abs(finiteOrNull(value) ?? 0);
  const capability = snapshot.capability || {};
  const metadata = capability.metadata || {};
  const results = (Array.isArray(snapshot.results) ? snapshot.results : [])
    .map((result) => ({
      value: Math.abs(standardResultValue(result) ?? 0),
      expanded: Math.abs(finiteOrNull(result.uncertainty_expanded) ?? 0),
      k: finiteOrNull(result.coverage_factor) ?? finiteOrNull(snapshot.certificate?.coverage_factor) ?? 2,
      unit: result.unit || capability.unit,
    }))
    .filter((result) => result.expanded > 0)
    .sort((a, b) => a.value - b.value);
  if (results.length) {
    const nearest = results.reduce((best, result) => (
      !best || Math.abs(result.value - target) < Math.abs(best.value - target) ? result : best
    ), null);
    const expandedBase = forceValueToBase(nearest.expanded, nearest.unit, metadata.subfamily || (isTorqueUnit(nearest.unit) ? 'torque' : 'force'));
    return nearest.k > 0 ? expandedBase / nearest.k : expandedBase / 2;
  }
  const relativePercent = finiteOrNull(metadata.certified_uncertainty_percent);
  if (relativePercent !== null && relativePercent > 0) {
    const k = finiteOrNull(snapshot.certificate?.coverage_factor) ?? 2;
    return (target * relativePercent / 100) / (k > 0 ? k : 2);
  }
  return null;
}

export function torqueTolerancePercent(toolClass, setpoint, manualPercent = 4) {
  const className = String(toolClass || '').trim().toUpperCase();
  const point = Math.abs(finiteOrNull(setpoint) ?? 0);
  if (['A', 'D', 'F', 'G'].includes(className)) return 6;
  if (['B', 'C', 'E'].includes(className)) return point < 10 ? 6 : 4;
  return Math.abs(finiteOrNull(manualPercent) ?? 4);
}

function averageEnvironmentTemperature(familyData) {
  return mean((familyData.environment_readings || []).map((item) => item?.temp_c));
}

export function computeForcePointMetrics(row = {}, session = {}, standardSnapshot = null, block = {}) {
  const familyData = mergeForceFamilyData(session.family_data);
  const repeatCount = Math.max(1, Number(familyData.repeat_count) || 5);
  const pairs = Array.from({ length: repeatCount }, (_, index) => ({
    pattern: finiteOrNull(row[`r${index + 1}_pattern`]),
    instrument: finiteOrNull(row[`r${index + 1}_instrument`]),
  })).filter((pair) => pair.pattern !== null && pair.instrument !== null);
  if (!pairs.length) return null;

  const patternMean = mean(pairs.map((pair) => pair.pattern));
  const instrumentMean = mean(pairs.map((pair) => pair.instrument));
  const errors = pairs.map((pair) => pair.instrument - pair.pattern);
  const errorAbs = mean(errors);
  const errorPct = patternMean === 0 ? null : (errorAbs / patternMean) * 100;
  const repeatability = sampleStandardDeviation(errors);
  const uRepeatability = repeatability / Math.sqrt(Math.max(1, pairs.length));
  const resolution = Math.abs(finiteOrNull(session.instrument_resolution) ?? 0);
  const uResolution = resolution / SQRT12;
  const uStandard = interpolateStandardUncertainty(standardSnapshot || {}, patternMean);
  if (uStandard === null) return null;

  const budget = familyData.standard_budget || {};
  const uDrift = Math.abs(patternMean) * (Math.abs(finiteOrNull(budget.drift_percent) ?? 0) / 100) / SQRT3;
  const averageTemp = averageEnvironmentTemperature(familyData);
  const tempDelta = averageTemp === null ? 0 : Math.abs(averageTemp - 20);
  const tempCoefficient = Math.abs(finiteOrNull(budget.temperature_coefficient_percent_per_10c) ?? 0);
  const uTemp = Math.abs(patternMean) * (tempCoefficient / 100) * (tempDelta / 10) / SQRT3;
  const uAlignment = Math.abs(patternMean) * (Math.abs(finiteOrNull(budget.alignment_uncertainty_percent) ?? 0) / 100) / SQRT3;
  const uOther = Math.abs(patternMean) * (Math.abs(finiteOrNull(budget.other_uncertainty_percent) ?? 0) / 100) / SQRT3;
  const uc = rss([uStandard, uDrift, uResolution, uRepeatability, uTemp, uAlignment, uOther]);
  const k = 2;
  const expanded = uc * k;

  const setpoint = finiteOrNull(row.setpoint) ?? patternMean;
  const tolerancePercent = familyData.tolerance_mode === 'class' && familyData.subfamily === 'torque'
    ? torqueTolerancePercent(familyData.tool_class, setpoint, familyData.tolerance_percent)
    : Math.abs(finiteOrNull(familyData.tolerance_percent) ?? 0);
  const toleranceAbs = familyData.tolerance_mode === 'absolute'
    ? Math.abs(finiteOrNull(session.instrument_class) ?? 0)
    : Math.abs(patternMean) * tolerancePercent / 100;
  const inTolerance = toleranceAbs > 0
    ? Math.abs(errorAbs) + (familyData.decision_rule === 'guard_band' ? expanded : 0) <= toleranceAbs
    : null;

  return {
    point_no: Number(row.point_no),
    block_id: block?.id || null,
    direction: block?.mode || 'LOAD',
    setpoint,
    pattern_mean: patternMean,
    instrument_mean: instrumentMean,
    error_abs: errorAbs,
    correction: -errorAbs,
    error_pct: errorPct,
    repeatability,
    u_standard: uStandard,
    u_drift: uDrift,
    u_resolution: uResolution,
    u_repeatability: uRepeatability,
    u_temp: uTemp,
    u_alignment: uAlignment,
    u_other: uOther,
    uc,
    k,
    u_expanded: expanded,
    u_reported: Math.max(expanded, resolution), // piso por resolucion del instrumento (QA 2026-09-06)
    tolerance_percent: tolerancePercent,
    tolerance_abs: toleranceAbs,
    in_tolerance: inTolerance,
    repeat_count: pairs.length,
  };
}

export function validateForceRows(rows = [], familyData = {}) {
  const merged = mergeForceFamilyData(familyData);
  const repeatCount = Math.max(1, Number(merged.repeat_count) || 5);
  const issues = [];
  rows.forEach((row, index) => {
    const point = Number(row.point_no) || index + 1;
    if (finiteOrNull(row.setpoint) === null) issues.push(`Punto ${point}: falta el valor nominal.`);
    for (let repeat = 1; repeat <= repeatCount; repeat += 1) {
      if (finiteOrNull(row[`r${repeat}_pattern`]) === null) issues.push(`Punto ${point}: falta la lectura ${repeat} del patrón.`);
      if (finiteOrNull(row[`r${repeat}_instrument`]) === null) issues.push(`Punto ${point}: falta la lectura ${repeat} del instrumento.`);
    }
  });
  return issues;
}
